import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart,
  Briefcase,
  Building,
  Calendar,
  Check,
  Clock,
  Code,
  CreditCard,
  Database,
  Diamond,
  DollarSign,
  Download,
  Flame,
  Globe,
  HelpCircle,
  Image,
  Key,
  Layers,
  Link as LinkIcon,
  Lock,
  Mail,
  MapPin,
  Maximize2,
  Palette,
  Rocket,
  Search,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  Users as Team,
  TrendingUp,
  UserCircle,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import ColorBends from "../components/ColorBends";
import WalletConnect from "../components/WalletConnect";
import { useAuth } from "../contexts/AuthContext";

const HomePage = () => {
  const { isAuthenticated, walletAddress } = useAuth();

  const coreFeatures = [
    {
      icon: Globe,
      title: "Decentralized Identity",
      description:
        "Your profile lives on IPFS, giving you true ownership of your digital identity. Publish once, access forever.",
    },
    {
      icon: Shield,
      title: "Web3 Native",
      description:
        "Built for crypto natives with wallet login, SIWE authentication, and onchain verification.",
    },
    {
      icon: Users,
      title: "Creator Focused",
      description:
        "Perfect for creators, influencers, and entrepreneurs to showcase their work and build their brand.",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description:
        "Optimized for speed with modern web technologies, IPFS distribution, and edge caching.",
    },
  ];

  const availableFeatures = [
    {
      title: "Wallet Authentication",
      description:
        "Connect with MetaMask, WalletConnect, Coinbase Wallet and 300+ wallets via Reown AppKit",
      icon: Key,
      category: "Authentication",
    },
    {
      title: "Customizable Themes",
      description:
        "Choose from multiple themes or create custom CSS/Tailwind variants",
      icon: Palette,
      category: "Customization",
    },
    {
      title: "Content Blocks",
      description:
        "Add links, images, videos, NFT widgets (soon), products, galleries, text, and more",
      icon: Layers,
      category: "Content",
    },
    {
      title: "Avatar Upload + SBT Integration",
      description:
        "Upload custom avatars or use Soulbound ForgeID headshots automatically",
      icon: UserCircle,
      category: "Profile",
    },
    {
      title: "IPFS Publishing",
      description:
        "Publish your profile to IPFS for permanent, decentralized storage",
      icon: Database,
      category: "Web3",
    },
    {
      title: "Analytics Dashboard",
      description: "Track views, clicks, traffic, leads, and connections",
      icon: BarChart,
      category: "Analytics",
    },
    {
      title: "Lead Capture",
      description: "Collect email and contact data with embeddable forms",
      icon: UserPlus,
      category: "Engagement",
    },
    {
      title: "Connection Management",
      description: "Add notes, tags, and meeting context for people you meet",
      icon: Team,
      category: "Networking",
    },
    {
      title: "QR Code Sharing",
      description: "Generate QR codes for events and networking",
      icon: Maximize2,
      category: "Sharing",
    },
    {
      title: "vCard Export",
      description:
        "Export your profile as a Smart vCard for mobile contact sharing",
      icon: Download,
      category: "Sharing",
    },
    {
      title: "SEO Optimization",
      description:
        "Meta tags, OpenGraph, Twitter cards — optimized for search and social previews",
      icon: Search,
      category: "Marketing",
    },
  ];

  const comingSoonFeatures = [
    {
      title: "NFC Business Cards",
      description: "Order physical NFC cards that link to your HubID profile",
      icon: CreditCard,
      category: "Physical",
    },
    {
      title: "Token-Gated Content",
      description: "Restrict content to holders of specific NFTs or tokens",
      icon: Lock,
      category: "Web3",
    },
    {
      title: "Tip Jar (Crypto Payments)",
      description: "Accept crypto tips directly on your profile",
      icon: DollarSign,
      category: "Monetization",
    },
    {
      title: "NFT Gallery Widgets",
      description: "Showcase your NFT collection with beautiful galleries",
      icon: Image,
      category: "Content",
    },
    {
      title: "Calendar Integration",
      description: "Add scheduling links (Cal.com, Calendly) to your profile",
      icon: Calendar,
      category: "Productivity",
    },
    {
      title: "Email Signature Generator",
      description: "Generate HTML email signatures with your profile link",
      icon: Mail,
      category: "Marketing",
    },
    {
      title: "Team/Organization Pages",
      description: "Create team profiles and manage multiple members",
      icon: Team,
      category: "Collaboration",
    },
    {
      title: "Advanced Analytics",
      description:
        "Device tracking, location insights, and traffic source analysis",
      icon: TrendingUp,
      category: "Analytics",
    },
    {
      title: "Premium Themes Marketplace",
      description: "Unlock exclusive premium themes with unique designs",
      icon: Star,
      category: "Customization",
    },
    {
      title: "API Access",
      description: "Integrate HubID profiles into your own applications",
      icon: Code,
      category: "Developer",
    },
    {
      title: "Wallet Passes (Apple/Google Wallet)",
      description: "Generate Apple Wallet and Google Wallet passes",
      icon: Smartphone,
      category: "Mobile",
    },
    {
      title: "CRM Integrations",
      description: "Sync connections and leads with popular CRM platforms",
      icon: Briefcase,
      category: "Business",
    },
  ];

  const benefits = [
    { text: "Customizable themes", icon: Palette },
    { text: "Showcase content & products", icon: LinkIcon },
    { text: "Accept crypto payments (soon)", icon: DollarSign },
    { text: "Analytics & insights", icon: BarChart },
    { text: "Lead capture forms", icon: UserPlus },
    { text: "ForgeID avatar support", icon: UserCircle },
    { text: "QR codes + vCard", icon: Smartphone },
    { text: "SEO optimized", icon: Search },
    { text: "ENS compatible", icon: Globe },
    { text: "Fully responsive", icon: Maximize2 },
    { text: "Non-custodial ownership", icon: Lock },
  ];

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="min-h-screen bg-dark-950 relative overflow-hidden">
      {/* Header */}
      <header className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-auto">
        <div className="px-4 py-2 flex items-center justify-between gap-8 rounded-full bg-black/20 backdrop-blur-md border border-white/10 shadow-lg">
          <div className="flex items-center">
            <img src="/HubID Logo White.svg" alt="HubID" className="h-6" />
          </div>
          <div className="flex items-center gap-6">
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-white text-sm hover:text-orange-400 transition-colors px-3 py-1.5 rounded-full hover:bg-white/5"
                >
                  My Profile
                </Link>
                <Link
                  to="/analytics"
                  className="text-white text-sm hover:text-orange-400 transition-colors px-3 py-1.5 rounded-full hover:bg-white/5"
                >
                  Credit History
                </Link>
                {walletAddress && (
                  <div className="px-3 py-1.5 rounded-full flex items-center gap-2 border border-orange-500/30 bg-orange-500/10 backdrop-blur-sm">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse-glow" />
                    <span className="text-orange-400 font-mono text-xs">
                      {formatAddress(walletAddress)}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <WalletConnect
                className="px-3 py-1.5 text-sm"
                style={{ borderRadius: "32px" }}
              />
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative px-4 pt-32 pb-20 overflow-hidden min-h-[600px] bg-transparent">
        {/* Color Bends Background Animation */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{ zIndex: 1, opacity: 0.9 }}
        >
          <ColorBends
            colors={["#FF6B35", "#FF8C42", "#FFA500", "#FFB84D"]}
            rotation={30}
            speed={0.3}
            scale={0.6}
            frequency={2.0}
            warpStrength={1.2}
            mouseInfluence={0.8}
            parallax={0.6}
            noise={0.08}
            transparent
          />
        </div>
        <div
          className="max-w-4xl mx-auto text-center relative"
          style={{ zIndex: 10, backgroundColor: "transparent" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-transparent"
          >
            <h1 className="text-4xl md:text-6xl font-semibold mb-6">
              <span className="text-orange-bright">Your Web3 Identity,</span>
              <br />
              <span className="text-white">All in One Link</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-xl md:text-2xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed"
            >
              Create a decentralized link-in-bio powered by your wallet, ENS,
              and Web3 identity.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Publish your profile to IPFS, customize your links and content
              blocks, and share a permanent identity page you truly own.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-wrap items-center justify-center gap-4 mb-6"
            >
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Lock className="w-4 h-4 text-orange-400" />
                <span>Non-custodial</span>
              </div>
              <span className="text-gray-600">•</span>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Globe className="w-4 h-4 text-orange-400" />
                <span>IPFS hosted</span>
              </div>
              <span className="text-gray-600">•</span>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <UserCircle className="w-4 h-4 text-orange-400" />
                <span>Wallet identity</span>
              </div>
              <span className="text-gray-600">•</span>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Sparkles className="w-4 h-4 text-orange-400" />
                <span>ForgeID compatible</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              {!isAuthenticated && <WalletConnect />}
              <Link
                to="/u/demo"
                className="glass px-6 py-3 rounded-lg text-white hover:text-orange-400 transition-colors border border-white/10 hover:border-orange-500/50"
              >
                View Demo Profile
                <ArrowRight className="ml-2 inline w-4 h-4" />
              </Link>
            </motion.div>
            {!isAuthenticated && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-sm text-orange-400 mt-4 flex items-center justify-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Get Started (Free During Beta)</span>
              </motion.p>
            )}
          </motion.div>
        </div>
      </section>

      {/* What is HubID? */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 flex items-center justify-center gap-3">
              <Sparkles className="w-8 h-8 text-orange-400" />
              <span>What is HubID?</span>
            </h2>
            <div className="glass-dark p-8 rounded-2xl border border-dark-700 text-left">
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                HubID.app is a{" "}
                <span className="text-orange-400 font-semibold">
                  decentralized identity page builder
                </span>{" "}
                for Web3 users, creators, founders, and communities.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                It&apos;s like a{" "}
                <span className="text-orange-400 font-semibold">
                  Web3 Linktree
                </span>{" "}
                — customizable, non-custodial, and stored on IPFS for
                censorship-resistant, portable identity.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Connect your wallet, load your ENS, add your links and media,
                select a theme, and publish a shareable page:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <div className="flex-1 glass p-4 rounded-lg border border-orange-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-orange-400" />
                    <p className="text-sm text-gray-400">HubID URL</p>
                  </div>
                  <p className="text-orange-400 font-mono">
                    hubid.app/username
                  </p>
                </div>
                <div className="flex-1 glass p-4 rounded-lg border border-orange-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-orange-400" />
                    <p className="text-sm text-gray-400">IPFS URL</p>
                  </div>
                  <p className="text-orange-400 font-mono text-sm">
                    ipfs://your-profile-cid
                  </p>
                </div>
              </div>
              <p className="text-lg text-orange-400 font-semibold mt-6">
                Own your profile forever.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Available Features */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
                <Flame className="w-8 h-8 text-orange-400" />
                <span>Available Features</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Everything you can do right now with HubID
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {availableFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group glass-dark p-6 rounded-xl border border-dark-700 hover:border-orange-500/50 transition-all duration-300 hover:shadow-glow-orange-sm cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-orange-500/30 group-hover:bg-orange-500/30 group-hover:border-orange-500/50 transition-all duration-300">
                    {React.createElement(feature.icon, {
                      className:
                        "text-orange-400 w-5 h-5 group-hover:scale-110 transition-transform duration-300",
                    })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-base font-semibold text-white group-hover:text-orange-400 transition-colors">
                        {feature.title}
                      </h3>
                      <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 animate-pulse-glow"></div>
                    </div>
                    <p className="text-sm text-dark-300 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="mt-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20">
                        {feature.category}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Features */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-dark-700 rounded-2xl mb-6 border border-dark-600">
                <Clock className="text-dark-400 w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
                <Rocket className="w-8 h-8 text-orange-400" />
                <span>Coming Soon</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Exciting features we&apos;re building for the future
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {comingSoonFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group glass-dark p-6 rounded-xl border border-dark-700 hover:border-dark-600 transition-all duration-300 opacity-80 hover:opacity-100 cursor-pointer relative overflow-hidden"
              >
                {/* Coming Soon Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-dark-700 text-dark-400 border border-dark-600">
                    <Clock className="mr-1 w-3 h-3" />
                    Soon
                  </span>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-dark-700 rounded-xl flex items-center justify-center flex-shrink-0 border border-dark-600 group-hover:bg-dark-600 group-hover:border-dark-500 transition-all duration-300">
                    {React.createElement(feature.icon, {
                      className:
                        "text-dark-400 w-5 h-5 group-hover:text-dark-300 transition-colors duration-300",
                    })}
                  </div>
                  <div className="flex-1 min-w-0 pr-8">
                    <h3 className="text-base font-semibold text-white mb-2 group-hover:text-dark-200 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-dark-400 leading-relaxed mb-3">
                      {feature.description}
                    </p>
                    <div>
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-dark-800 text-dark-500 border border-dark-700">
                        {feature.category}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose HubID? */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <Briefcase className="w-8 h-8 text-orange-400" />
              <span>Why Choose HubID?</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Your identity, your rules.
            </p>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              HubID is a portable profile you own, connected to your wallet and
              optionally published to IPFS.
            </p>
          </div>
          <div className="glass-dark p-8 rounded-2xl border border-dark-700 mb-8">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">
              Everything in one place
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-dark-700 hover:border-orange-500/30 transition-colors"
                >
                  {React.createElement(benefit.icon, {
                    className: "text-orange-400 w-4 h-4 flex-shrink-0",
                  })}
                  <span className="text-gray-300 text-sm">{benefit.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <DollarSign className="w-8 h-8 text-orange-400" />
              <span>Pricing</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-4">
              All plans free during beta for early users.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "Free",
                price: "$0",
                features: [
                  "1 profile",
                  "3 themes",
                  "Wallet login",
                  "Content blocks",
                  "Basic analytics",
                  "QR sharing",
                  "vCard",
                  "IPFS publishing",
                ],
              },
              {
                name: "Pro",
                price: "$8/mo",
                features: [
                  "Custom domain",
                  "Unlimited links",
                  "Premium themes",
                  "Extended analytics",
                  "Email signatures",
                  "Lead capture",
                ],
                popular: false,
              },
              {
                name: "Creator",
                price: "$19/mo",
                features: [
                  "Verified badge",
                  "NFT gallery",
                  "Tipping",
                  "Store links",
                  "ENS auto-update",
                  "Advanced stats",
                ],
                popular: true,
              },
              {
                name: "Enterprise/Team",
                price: "Custom",
                features: [
                  "Multi-member portals",
                  "DAO identity hubs",
                  "CRM sync",
                  "API access",
                  "Dedicated onboarding",
                ],
              },
            ].map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`glass-dark p-6 rounded-xl border ${
                  plan.popular
                    ? "border-orange-500/50 shadow-glow-orange-sm"
                    : "border-dark-700"
                } relative`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Popular
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <div className="text-3xl font-bold text-orange-400 mb-4">
                  {plan.price}
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start space-x-2">
                      <Check className="text-orange-400 w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {!isAuthenticated && <WalletConnect variant="secondary" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <HelpCircle className="w-8 h-8 text-orange-400" />
              <span>FAQ</span>
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "What makes HubID different from Linktree?",
                icon: Diamond,
                a: "Linktree is centralized and account-based. HubID is wallet-based, non-custodial, and can be published to IPFS, making it permanent and censorship-resistant.",
              },
              {
                q: "Do I need crypto to use HubID?",
                icon: UserCircle,
                a: "No. Wallet login is free, and IPFS publishing is covered during beta.",
              },
              {
                q: "Can I use my ENS name?",
                icon: LinkIcon,
                a: "Yes — you can map your ENS contenthash directly to your HubID IPFS page.",
              },
              {
                q: "Do I need a ForgeID avatar?",
                icon: Sparkles,
                a: "No, it's optional. You may upload your own image anytime.",
              },
              {
                q: "Can teams or DAOs use HubID?",
                icon: Building,
                a: "Yes — team profiles and organization features are coming soon.",
              },
              {
                q: "Will my HubID page stay online forever?",
                icon: Globe,
                a: "Once published to IPFS, your identity is stored permanently.",
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-dark p-6 rounded-xl border border-dark-700"
              >
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  {React.createElement(faq.icon, {
                    className: "w-5 h-5 text-orange-400",
                  })}
                  <span>{faq.q}</span>
                </h3>
                <p className="text-gray-300 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative px-4 py-20" style={{ zIndex: 10 }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-orange p-12 rounded-2xl border border-orange-500/30">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <Rocket className="w-8 h-8 text-orange-400" />
              <span>Own Your Web3 Identity</span>
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              HubID is more than a profile.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              It&apos;s your portable identity layer for the decentralized web —
              verified by your wallet, preserved by IPFS, and enhanced by
              ForgeID.
            </p>
            {!isAuthenticated && (
              <div className="flex justify-center">
                <WalletConnect variant="secondary" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="relative bg-black/80 backdrop-blur-sm border-t border-white/10 py-12 px-4"
        style={{ zIndex: 10 }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src="/HubID Logo White.svg" alt="HubID" className="h-8" />
            </div>
            <div className="text-gray-400 text-center md:text-right">
              <p>&copy; 2025 HubID. Built for the decentralized web.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
